// @boty/ui - BidiText component
// Handles bidirectional text isolation for LTR content in RTL contexts

import type { ReactNode, CSSProperties } from 'react';

/**
 * Types of content that are always LTR, regardless of surrounding context
 */
export type BidiContentType = 'url' | 'email' | 'code' | 'phone' | 'number';

export interface BidiTextProps {
  /**
   * The content to render
   */
  children: ReactNode;
  /**
   * Type of content that should be isolated as LTR.
   * If not provided, children are rendered as-is without isolation.
   */
  type?: BidiContentType;
  /**
   * Additional CSS styles to apply to the wrapper
   */
  style?: CSSProperties;
  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * BidiText - Bidirectional text isolation component
 *
 * Use this component to wrap content that should always be displayed in LTR
 * direction, even when the surrounding context is RTL. This is essential for:
 * - URLs (https://example.com)
 * - Email addresses (user@example.com)
 * - Code snippets (function myFunc())
 * - Phone numbers (+1-555-123-4567)
 * - Numbers with units (100px, $50.00)
 *
 * The component uses the HTML `<bdi>` element with `dir="ltr"` to isolate
 * the content from bidirectional text reordering.
 *
 * @param children - The content to render
 * @param type - Type of content that should be isolated as LTR
 * @param style - Additional CSS styles
 * @param className - Additional CSS class name
 *
 * @example
 * ```tsx
 * // URL in RTL context
 * <p>
 *   בקר באתר שלנו: <BidiText type="url">https://example.com</BidiText>
 * </p>
 *
 * // Email in RTL context
 * <p>
 *   צור קשר: <BidiText type="email">support@boty.ai</BidiText>
 * </p>
 *
 * // Code in RTL context
 * <p>
 *   הפעל את הפונקציה <BidiText type="code">myFunction()</BidiText>
 * </p>
 * ```
 */
export function BidiText({
  children,
  type,
  style,
  className,
}: BidiTextProps): ReactNode {
  // If a type is specified, wrap in bdi with LTR direction
  if (type) {
    return (
      <bdi dir="ltr" style={style} className={className}>
        {children}
      </bdi>
    );
  }

  // If no type specified, render children as-is
  return <>{children}</>;
}

BidiText.displayName = 'BidiText';
